import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { featureName } from './constants';
import { ApiStatus, PaginatedList } from 'services/commonTypes';
import {
  ICreditCardSearchParams,
  ICreditCardSummary,
  ICreditCardTransaction,
} from './types';
const createAsync = geneAsyncAction(featureName);

const api = {
  get: {
    creditCard: createAsync<
      ICreditCardSearchParams,
      PaginatedList<ICreditCardTransaction>
    >('getCreditCard'),
    summary: createAsync<
      { startDate: string; endDate: string },
      ICreditCardSummary
    >('getSummary'),
  },
};

type SliceState = {
  transactions: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<ICreditCardTransaction>;
    searchParams?: ICreditCardSearchParams;
  };
  summary: {
    status: ApiStatus;
    error?: string;
    data?: ICreditCardSummary;
  };
};

type MyState = SliceState;

const reducers = {};
// @ts-ignore
const getCurrentState = (state: RootState): MyState => state['creditCard']; // ex: state['booking']

const select = <T = MyState>(key: keyof T, defaultValue?: any) =>
  useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

export const use = {
  creditCard: () => select('transactions'),
  summary: () => select('summary'),
};

const slice = createSlice({
  name: featureName,
  initialState: {
    transactions: { status: undefined },
    summary: { status: undefined },
  } as MyState,
  reducers,
  extraReducers: (builder) => {
    // Get Credit Card Transactions
    builder
      .addCase(api.get.creditCard.fetch, (state, { payload }) => {
        state.transactions.status = 'fetching';
        state.transactions.error = undefined;
        state.transactions.searchParams = payload;
      })
      .addCase(api.get.creditCard.success, (state, { payload }) => {
        state.transactions.status = 'success';
        state.transactions.data = payload;
      })
      .addCase(api.get.creditCard.fail, (state, { payload }) => {
        state.transactions.status = 'error';
        state.transactions.error = payload;
      });

    builder
      .addCase(api.get.summary.fetch, (state) => {
        state.summary.status = 'fetching';
        state.summary.error = undefined;
      })
      .addCase(api.get.summary.success, (state, { payload }) => {
        state.summary.status = 'success';
        state.summary.data = payload;
      })
      .addCase(api.get.summary.fail, (state, { payload }) => {
        state.summary.status = 'error';
        state.summary.error = payload;
      });
  },
});

export const actions = { ...slice.actions, api };
export const reducer = slice.reducer;
